import _ from "lodash";
import { ApiCallConfig, ScrapingMethod } from "routes/dashboard/ApiPlaygroundTypes";
import { SdeTrackingInfo } from "sdecontent";

const GTAG_ID = 'G-H7B6YKSKMX';

let gtagInitialzed = false;
const ensureGtagInitialized = () => {
  if (gtagInitialzed) {
    return;
  }

  if ((window as any).dataLayer === undefined) {
    console.warn("window.dataLayer is missing. Can't init gtag");
    return;
  }

  window.gtag = function () { (window as any).dataLayer.push(arguments); }

  console.log('Initializing gtag');
  window.gtag('js', new Date());
  window.gtag('config', GTAG_ID);

  gtagInitialzed = true;
}

const ga4Tracking = (eventName: string, params: any) => {
  try {
    ensureGtagInitialized();
    if (window?.gtag) {
      window?.gtag("event", eventName, { event_category: "engagement", ...params });
    }
  } catch (e) {
    console.error(e);
  }
};

type CopyMethod = 'ctrlC' | 'copyButton' | 'downloadButton';

export const trackApiPlaygroundDataCopied = (copyMethod: CopyMethod, config: ApiCallConfig, credits: number | undefined) => {
  ga4Tracking('api_playground_data_copied', { copy_method: copyMethod, url: config.input, scrapingMethod: config.scrapingMethod, credits, config: config.config });
};

export const trackApiPlaygroundURLEntered = (url: string | undefined) => {
  ga4Tracking('api_playground_url_entered', { url });
};

export const trackApiPlaygroundParameterChanged = (parameter: string, newValue: unknown) => {
  ga4Tracking('api_playground_parameter_changed', { parameter, new_value: newValue });
};

export const trackApiPlaygroundLanguageSelectorClicked = (selectedLanguage: string | undefined) => {
  ga4Tracking('api_playground_language_selector_clicked', { selected_language: selectedLanguage });
};

export const trackApiPlaygroundCopyToClipboardClicked = () => {
  ga4Tracking('api_playground_copy_to_clipboard_clicked', {});
};

export const trackApiPlaygroundSdeSelected = (selectedSDE: string) => {
  ga4Tracking('api_playground_sde_selected', { selected_sde: selectedSDE });
};

export const trackApiPlaygroundTryItClicked = (sde: SdeTrackingInfo, scrapingMethod: string) => {
  ga4Tracking('api_playground_try_it_clicked', { ...sde, scraping_method: scrapingMethod });
};

// Data Pipeline SDE tracking. Every event carries sde_type/sde_name so the exact SDE is always identifiable.
export const trackDataPipelineSourceSelected = (sde: SdeTrackingInfo) => {
  ga4Tracking('data_pipeline_source_selected', { ...sde });
};

export const trackDataPipelineProjectCreated = (sde: SdeTrackingInfo, started: boolean) => {
  ga4Tracking('data_pipeline_project_created', { ...sde, started });
};

export const trackDataPipelineRunJobNow = (sde: SdeTrackingInfo, projectId: number) => {
  ga4Tracking('data_pipeline_run_job_now', { ...sde, project_id: projectId });
};

export const trackDownloadReportClicked = (option: string) => {
  ga4Tracking('new_design_download_report', { option });
};

export const trackLastUpdatedClicked = (option: string) => {
  ga4Tracking('new_design_last_updated', { option });
};

export const trackRecentlyViewedOnClick = (option: string) => {
  ga4Tracking('new_design_recently_viewed', { option });
}

export const trackRecentlyViewedPinnedPageOnClick = (option: string) => {
  let formattedOption = option;

  if (formattedOption === 'DataPipeline Projects') {
    formattedOption = 'dp_projects';
  } else if (formattedOption.includes('DataPipeline Projects /')) {
    const [, subOption] = formattedOption.split('/');
    formattedOption = `dp_${_.camelCase(subOption).toLowerCase().trim()}`;
  } else {
    formattedOption = _.snakeCase(option).toLowerCase();
  }

  ga4Tracking(`new_design_pinned_${formattedOption}`, { option });
};

export const trackApiKeyCopiedOnClick = (option: string) => {
  ga4Tracking('new_design_api_key_copied', { option });
}

export const trackWhatsNewLinkOnClick = (option: string) => {
  ga4Tracking('new_design_whats_new_link_clicked', { option });
}

export const trackNeedHelpLinkOnClick = (option: string) => {
  ga4Tracking('new_design_need_help_link_clicked', { option });
}

export const trackWelcomeDashboardScrapeChange = (option: string) => {
  let currentOption: string = option;
  if (currentOption === 'StructuredData') {
    currentOption = 'sde';
  }
  ga4Tracking(`new_design_welcome_scrape_method_${currentOption}`, { currentOption });
}

export const trackWelcomeDashboardProgrammingLanguageChange = (option: string) => {
  ga4Tracking(`new_design_welcome_language_${option}`, { option });
}

export const trackWelcomeDashboardCopySnippet = (option: string) => {
  ga4Tracking(`new_design_welcome_copy_snippet`, { option });
}

export const trackWelcomeDashboardGoToApiPlayground = (option: string) => {
  ga4Tracking(`new_design_welcome_goto_apipg`, { option });
}

export const trackScrapingMethodOnChange = (option: ScrapingMethod) => {
  let currentOption: string = option;

  if (option === 'structured_data_endpoint') {
    currentOption = 'sde';
  }
  ga4Tracking(`apipg_chosen_scrape_method_${currentOption}`, { currentOption });
}

export const trackRenewalChanges = (option: string) => {
  ga4Tracking(`billing_renewal_option_${option}`, { option });
}

export const trackBillingClickAway = (option: string) => {
  ga4Tracking(`billing_click_away_${option}`, { option });
}

// v2 Billing tracking. "_submitted" events represent frontend intent, not backend outcome.
export const trackBillingV2PageViewed = (params: { current_plan?: string } = {}) => {
  ga4Tracking('billing_v2_page_viewed', params);
}

export const trackBillingV2PageLoadFailed = (params: { error_code?: string }) => {
  ga4Tracking('billing_v2_page_load_failed', params);
}

export const trackBillingV2PageLoaded = (params: { load_time_ms: number }) => {
  ga4Tracking('billing_v2_page_loaded', params);
}

export const trackBillingV2TabViewed = (tab: 'subscription' | 'billing_details' | 'manage_plan') => {
  ga4Tracking(`billing_v2_${tab}_tab_viewed`, { tab });
}

export const trackBillingV2UpgradeClicked = (params: { source: string; current_plan?: string; target_plan?: string; billing_period?: string }) => {
  ga4Tracking('billing_v2_upgrade_clicked', params);
}

export const trackBillingV2AnnualToMonthlyClicked = (params: { source: string; current_plan?: string }) => {
  ga4Tracking('billing_v2_annual_to_monthly_clicked', params);
}

export const trackBillingV2ViewAllPlansClicked = (source: string) => {
  ga4Tracking('billing_v2_view_all_plans_clicked', { source });
}

export const trackBillingV2ResubscribeClicked = (plan: string) => {
  ga4Tracking('billing_v2_resubscribe_clicked', { plan });
}

export const trackBillingV2ContactUsClicked = (source: string, plan?: string) => {
  ga4Tracking('billing_v2_contact_us_clicked', { source, plan });
}

export const trackBillingV2CancelClicked = (params: { plan?: string } = {}) => {
  ga4Tracking('billing_v2_cancel_clicked', params);
}

export const trackBillingV2ContactSalesFormSubmitted = () => {
  ga4Tracking('billing_v2_contact_sales_form_submitted', {});
}

// "_submitted" events represent frontend intent, not backend outcome.
export const trackBillingV2AutoUpgradeChangeInitiated = (params: { enabled: boolean; source?: string }) => {
  ga4Tracking('billing_v2_auto_upgrade_change_initiated', params);
}

export const trackBillingV2AutoUpgradeChangeSucceeded = (params: { enabled: boolean; source?: string }) => {
  ga4Tracking('billing_v2_auto_upgrade_change_succeeded', params);
}

export const trackBillingV2PaygChangeInitiated = (params: { enabled: boolean; source?: string }) => {
  ga4Tracking('billing_v2_payg_change_initiated', params);
}

export const trackBillingV2PaygChangeSubmitted = (params: { enabled: boolean; source?: string }) => {
  ga4Tracking('billing_v2_payg_change_submitted', params);
}

export const trackBillingV2PaygChangeSucceeded = (params: { enabled: boolean; source?: string }) => {
  ga4Tracking('billing_v2_payg_change_succeeded', params);
}

export const trackBillingV2UpgradeSubmitted = (params: { current_plan?: string; target_plan?: string }) => {
  ga4Tracking('billing_v2_upgrade_submitted', params);
}

export const trackBillingV2DowngradeSubmitted = (params: { current_plan?: string; target_plan?: string }) => {
  ga4Tracking('billing_v2_downgrade_submitted', params);
}

export const trackBillingV2BillingPeriodToggled = (params: { period: 'monthly' | 'yearly'; source: string }) => {
  ga4Tracking('billing_v2_billing_period_toggled', params);
}

// Buckets by viewport width using Tailwind's md (768) / lg (1024) breakpoints — the app's own
// media-query boundaries — so the PM can pivot on device type without registering GA4 metrics.
const viewportDeviceBucket = (width: number): 'mobile' | 'tablet' | 'desktop' => {
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Captures the browser viewport (what actually drives the View All Plans clipping bug) plus
// physical screen, so we can estimate how many/which devices are affected before a redesign.
export const trackBillingV2ViewAllPlansViewport = () => {
  const viewportWidth = window.innerWidth;
  ga4Tracking('billing_v2_view_all_plans_viewport', {
    viewport_width: viewportWidth,
    viewport_height: window.innerHeight,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    device_pixel_ratio: window.devicePixelRatio,
    device_bucket: viewportDeviceBucket(viewportWidth),
  });
};

export const trackBillingV2ModalOpened = (
  modal:
    | 'view_all_plans'
    | 'upgrade_complete'
    | 'downgrade'
    | 'delete_account'
    | 'payg_enable'
    | 'contact_sales'
    | 'remove_primary_payment_method'
) => {
  ga4Tracking('billing_v2_modal_opened', { modal });
}

export const trackPaymentMethodAddSubmitted = (methodType: string) => {
  ga4Tracking('billing_details_payment_method_add_submitted', { method_type: methodType });
}

export const trackPaymentMethodAddSucceeded = (methodType: string) => {
  ga4Tracking('billing_details_payment_method_add_succeeded', { method_type: methodType });
}

export const trackPaymentMethodRemoveSubmitted = (methodType: string) => {
  ga4Tracking('billing_details_payment_method_remove_submitted', { method_type: methodType });
}

export const trackPaymentMethodRemoveSucceeded = (params: { method_type: string; was_primary: boolean }) => {
  ga4Tracking('billing_details_payment_method_remove_succeeded', params);
}

export const trackPaymentMethodSetPrimarySubmitted = (methodType: string) => {
  ga4Tracking('billing_details_payment_method_set_primary_submitted', { method_type: methodType });
}

export const trackBillingAddressUpdated = () => {
  ga4Tracking('billing_details_billing_address_updated', {});
}

export const trackPromoCodeApplySubmitted = (couponCode: string | undefined) => {
  if (!!couponCode) {
    ga4Tracking('billing_details_promo_code_apply_submitted', { coupon_code: couponCode });
  }
}

export const trackInvoiceDownloadClicked = (invoiceId: string, invoiceNumber?: string) => {
  ga4Tracking('billing_details_invoice_download_clicked', {
    invoice_id: invoiceId,
    invoice_number: invoiceNumber
  });
}

export const trackBillingV2InvoiceFilterChanged = (filter: string) => {
  ga4Tracking('billing_v2_invoice_filter_changed', { filter });
}

export const trackBillingV2InvoicePayNowClicked = (params: { invoice_id: string }) => {
  ga4Tracking('billing_v2_invoice_pay_now_clicked', params);
}

// analytics tracking
export const trackAnalyticsTabViewed = (section: string = 'overview') =>
  ga4Tracking('analytics_tab_viewed', { section });

export const trackAnalyticsSubpageViewed = (p: { section: string; subsection?: string }) =>
  ga4Tracking('analytics_subpage_viewed', p);

export const trackAnalyticsActionClicked = (p: { section: string; action: string; target?: string;}) =>
  ga4Tracking('analytics_action_clicked', p);

export const trackAnalyticsReportDownloaded = (section: string) =>
  ga4Tracking('analytics_report_downloaded', { section });

export const trackMoreApisClaimClicked = (apiName: string) => {
  ga4Tracking('more_apis_claim_clicked', { api_name: apiName });
};

export const trackMoreApisViewDocsClicked = (apiName: string) => {
  ga4Tracking('more_apis_view_docs_clicked', { api_name: apiName });
};

export const trackMoreApisCopyCodeClicked = () => {
  ga4Tracking('more_apis_copy_code_clicked', {});
};
